import React from 'react';
import { client } from '../../lib/sanity';
import { useNavigate } from 'react-router-dom';
import { Heart, ArrowRight } from 'lucide-react';
import { getPetUrlIdentifier } from '../../lib/petUtils';
import type { Pet } from '../../types/sanity';
import { PetCard } from '../pets/PetCard';
import { LoadingMessage } from '../common/LoadingMessage';
import { ErrorMessage } from '../common/ErrorMessage';

export function FeaturedPetsHome() {
  const [pets, setPets] = React.useState<Pet[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const navigate = useNavigate();

  const calculateAge = (birthDate: string) => {
    const today = new Date();
    const birth = new Date(birthDate);
    const ageInMonths = (today.getFullYear() - birth.getFullYear()) * 12 + 
      (today.getMonth() - birth.getMonth());
    
    if (ageInMonths < 12) {
      return `${ageInMonths} mies.`;
    }
    
    const age = Math.floor(ageInMonths / 12);
    return `${age} ${age === 1 ? 'rok' : age < 5 ? 'lata' : 'lat'}`;
  };

  const handleMeetPet = (identifier: string) => {
    navigate(`/zwierze/${identifier}`);
  };

  React.useEffect(() => {
    const fetchFeaturedPets = async () => {
      try {
        const result = await client.fetch<Pet[]>(`
          *[_type == "pet" && isFeatured == true && !(_id in path("drafts.**"))] | order(_createdAt desc)[0...6] {
            _id,
            name,
            slug,
            birthDate,
            image,
            description,
            gender,
            size,
            status,
            isUrgentFundraising,
            fundraisingGoal,
            fundraisingCurrent,
            fundraisingDescription,
            "tags": tags[]-> {
              _type,
              _ref,
              label,
              "slug": slug
            },
            mainCategory,
            gallery
          }
        `);

        setPets(result);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching featured pets:', err);
        setError('Nie udało się załadować wyróżnionych zwierząt');
        setLoading(false);
      }
    };

    fetchFeaturedPets();
  }, []);

  if (loading) {
    return (
      <section className="py-16 bg-white dark:bg-accessible-gray-800 high-contrast:bg-black">
        <div className="max-w-7xl mx-auto px-4">
          <LoadingMessage ariaLabel="Ładowanie wyróżnionych zwierząt" />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 bg-white dark:bg-accessible-gray-800 high-contrast:bg-black">
        <div className="max-w-7xl mx-auto px-4">
          <ErrorMessage message={error} ariaLabel="Błąd ładowania wyróżnionych zwierząt" />
        </div>
      </section>
    );
  }

  if (pets.length === 0) {
    return null;
  }

  return (
    <section className="py-[64px] px-[48px] bg-white dark:bg-accessible-gray-800 high-contrast:bg-black">
      <div className="max-w-7xl mx-auto px-[16px]">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-[24px] mb-[48px]">
          <div>
            <h2 className="section-title mb-4">
              Wyróżnione zwierzaki
            </h2>
            <p className="body-large max-w-2xl no-margin">
              Poznaj niektórych z naszych podopiecznych czekających na nowy dom
            </p>
          </div>
          <button
            onClick={() => navigate('/zwierzeta')}
            className="hidden md:inline-flex items-center gap-2 px-6 py-3 bg-accessible-red-600 hover:bg-accessible-red-700 text-white rounded-lg transition-colors group btn-text-medium"
            aria-label="Zobacz wszystkie zwierzęta do adopcji"
          >
            <span className="btn-text-medium">Zobacz wszystkie</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-[32px] mb-[48px]" aria-label="Wyróżnione zwierzaki do adopcji">
          {pets.map((pet) => (
            <PetCard
              key={pet._id}
              pet={pet}
              onMeetPet={handleMeetPet}
              calculateAge={calculateAge}
            />
          ))}
        </div>

        <div className="text-center">
          <button
            onClick={() => navigate('/zwierzeta')}
            className="md:hidden inline-flex items-center gap-2 px-6 py-3 bg-accessible-red-600 hover:bg-accessible-red-700 text-white rounded-lg transition-colors group btn-text-medium"
            title="Zobacz wszystkie zwierzęta do adopcji"
          >
            <Heart className="w-5 h-5" />
            <span>Zobacz wszystkie zwierzaki</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
}